import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  AppBar,
  Toolbar,
  InputAdornment,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Snackbar,
  Alert,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

const Sucursales = () => {
  const [sucursales, setSucursales] = useState([]);
  const [originalSucursales, setOriginalSucursales] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [selectedSucursal, setSelectedSucursal] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    id_pet: '',
    nombre_sucursal: '',
    es_nopago: false,
    telefonos_sucursal: '',
    direccion_sucursal: '',
    horario_sucursal: '',
    ip_privada: '',
    latitud: '',
    longitud: '',
  });
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userIdPet, setUserIdPet] = useState(null);
  const [userIdRol, setUserIdRol] = useState(null);
  const [userSucursales, setUserSucursales] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const navigate = useNavigate();

  // Decodificar token JWT para obtener id_pet, id_rol y sucursales
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        console.log('Payload del token:', payload);
        setUserIdPet(payload.id_pet || null);
        setUserIdRol(payload.id_rol || null);
        setUserSucursales(payload.sucursales || []);
      } catch (err) {
        console.error('Error al decodificar el token:', err);
        const errorMsg = 'Error al decodificar el token de usuario';
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
      }
    } else {
      const errorMsg = 'No se encontró un token de usuario';
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  }, []);

  // Formatear fechas
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Normalizar datos de sucursal
  const normalizeSucursal = (sucursal) => {
    if (!sucursal || !sucursal.id_sucursal) {
      console.warn('Sucursal inválida recibida:', sucursal);
      return null;
    }
    return {
      id_sucursal: sucursal.id_sucursal ?? null,
      id_pet: sucursal.id_pet ?? null,
      nombre_sucursal: sucursal.nombre_sucursal ?? '',
      es_nopago: sucursal.es_nopago ?? false,
      telefonos_sucursal: sucursal.telefonos_sucursal ?? '',
      direccion_sucursal: sucursal.direccion_sucursal ?? '',
      horario_sucursal: sucursal.horario_sucursal ?? '',
      ip_privada: sucursal.ip_privada ?? '',
      latitud: sucursal.latitud ?? '',
      longitud: sucursal.longitud ?? '',
      createdAt: sucursal.createdAt ?? null,
      updatedAt: sucursal.updatedAt ?? null,
    };
  };

  // Obtener sucursales y pets
  useEffect(() => {
    const fetchData = async () => {
      if (!userIdPet || !userIdRol) {
        console.log('Esperando id_pet o id_rol del usuario...');
        return;
      }
      try {
        setLoading(true);
        const sucursalesRes = await api.get('/sucursales/');
        console.log('Datos crudos de GET /sucursales:', sucursalesRes.data);
        const sucursalesData = Array.isArray(sucursalesRes.data)
          ? sucursalesRes.data.filter(item => item !== null && item !== undefined)
          : [];
        const allowedPets = [userIdPet, ...userSucursales];
        const normalizedSucursales = sucursalesData
          .map(normalizeSucursal)
          .filter(sucursal => sucursal !== null && sucursal.id_sucursal && allowedPets.includes(sucursal.id_pet));
        console.log('Sucursales normalizadas:', normalizedSucursales);
        setSucursales(normalizedSucursales);
        setOriginalSucursales(normalizedSucursales);

        const petsRes = await api.get('/pets/');
        console.log('Respuesta de pets:', petsRes.data);
        setPets(Array.isArray(petsRes.data) ? petsRes.data.filter(pet => allowedPets.includes(pet.id_pet)) : []);
        setLoading(false);
      } catch (error) {
        console.error('Error al cargar datos:', error);
        const errorMsg = 'Error al cargar datos: ' + (error.response?.data?.detail || error.message || JSON.stringify(error));
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        setLoading(false);
      }
    };
    fetchData();
  }, [userIdPet, userIdRol, userSucursales]);

  // Buscar sucursales en frontend
  useEffect(() => {
    const searchSucursales = () => {
      console.log('Buscando con término:', searchTerm);
      console.log('Sucursales originales:', originalSucursales);
      if (!searchTerm) {
        setSucursales(originalSucursales);
        console.log('Restaurando sucursales:', originalSucursales);
        return;
      }
      const filteredSucursales = originalSucursales
        .filter(sucursal => sucursal && sucursal.id_sucursal && sucursal.nombre_sucursal)
        .filter(sucursal => sucursal.nombre_sucursal.toLowerCase().includes(searchTerm.toLowerCase()));
      console.log('Sucursales filtradas:', filteredSucursales);
      setSucursales(filteredSucursales);
      setPage(0);
    };
    searchSucursales();
  }, [searchTerm, originalSucursales]);

  // Abrir modal para crear/editar sucursal
  const handleOpenModal = (sucursal = null) => {
    setSelectedSucursal(sucursal);
    setFormData(
      sucursal
        ? normalizeSucursal(sucursal) || {
            id_pet: userIdPet || '',
            nombre_sucursal: '',
            es_nopago: false,
            telefonos_sucursal: '',
            direccion_sucursal: '',
            horario_sucursal: '',
            ip_privada: '',
            latitud: '',
            longitud: '',
          }
        : {
            id_pet: userIdPet || '',
            nombre_sucursal: '',
            es_nopago: false,
            telefonos_sucursal: '',
            direccion_sucursal: '',
            horario_sucursal: '',
            ip_privada: '',
            latitud: '',
            longitud: '',
          }
    );
    setOpenModal(true);
  };

  // Cerrar Snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setSnackbarMessage('');
  };

  // Guardar sucursal (crear o editar)
  const handleSave = async () => {
    try {
      if (!formData.nombre_sucursal || !formData.id_pet) {
        const errorMsg = 'Nombre de sucursal y veterinaria son obligatorios';
        setError(errorMsg);
        setSnackbarMessage(errorMsg);
        setSnackbarOpen(true);
        return;
      }
      const payload = {
        id_pet: formData.id_pet,
        nombre_sucursal: formData.nombre_sucursal,
        es_nopago: formData.es_nopago,
        telefonos_sucursal: formData.telefonos_sucursal || null,
        direccion_sucursal: formData.direccion_sucursal || null,
        horario_sucursal: formData.horario_sucursal || null,
        ip_privada: formData.ip_privada || null,
        latitud: formData.latitud ? parseFloat(formData.latitud) : null,
        longitud: formData.longitud ? parseFloat(formData.longitud) : null,
      };
      console.log('Guardando sucursal, payload:', payload);
      if (selectedSucursal) {
        const response = await api.put(`/sucursales/${selectedSucursal.id_sucursal}`, payload);
        console.log('Respuesta de PUT /sucursales:', response.data);
        const updatedSucursal = normalizeSucursal(response.data);
        if (updatedSucursal) {
          const newSucursales = sucursales.map(s => (s.id_sucursal === selectedSucursal.id_sucursal ? updatedSucursal : s));
          setSucursales(newSucursales);
          setOriginalSucursales(newSucursales);
          setSnackbarMessage('Sucursal actualizada exitosamente');
          setSnackbarOpen(true);
        }
      } else {
        const response = await api.post('/sucursales/', payload);
        console.log('Respuesta de POST /sucursales:', response.data);
        const newSucursal = normalizeSucursal(response.data);
        if (newSucursal) {
          const newSucursales = [...sucursales, newSucursal];
          setSucursales(newSucursales);
          setOriginalSucursales(newSucursales);
          setSnackbarMessage('Sucursal creada exitosamente');
          setSnackbarOpen(true);
        }
      }
      setOpenModal(false);
      setError(null);
      setPage(0);
    } catch (error) {
      console.error('Error al guardar sucursal:', error);
      const errorMsg = 'Error al guardar sucursal: ' + (error.response?.data?.detail || error.message || JSON.stringify(error));
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Eliminar sucursal
  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta sucursal?')) return;
    try {
      console.log('Enviando DELETE /sucursales/', id);
      const response = await api.delete(`/sucursales/${id}`);
      console.log('Respuesta de DELETE /sucursales:', response.data);
      const newSucursales = sucursales.filter(s => s.id_sucursal !== id);
      setSucursales(newSucursales);
      setOriginalSucursales(newSucursales);
      setSnackbarMessage('Sucursal eliminada exitosamente');
      setSnackbarOpen(true);
      setPage(0);
    } catch (error) {
      console.error('Error al eliminar sucursal:', error);
      const errorMsg = 'Error al eliminar sucursal: ' + (error.response?.data?.detail || error.message || JSON.stringify(error));
      setError(errorMsg);
      setSnackbarMessage(errorMsg);
      setSnackbarOpen(true);
    }
  };

  // Manejo de paginación
  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const paginatedSucursales = sucursales.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ p: 3 }}>
      <AppBar position="relative" sx={{ bgcolor: '#1e293b', mb: 3 }}>
        <Toolbar>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Gestión de Sucursales
          </Typography>
          <TextField
            variant="outlined"
            placeholder="Buscar sucursales..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ bgcolor: 'white', borderRadius: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>
      </AppBar>
      <Container maxWidth={false} disableGutters>
        {loading || !userIdPet || !userIdRol ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center">{error}</Typography>
        ) : sucursales.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6">Sin datos</Typography>
            <Typography color="text.secondary">
              No se encontraron sucursales. Crea una nueva sucursal para comenzar.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenModal()}
              sx={{ mt: 2 }}
            >
              Nueva Sucursal
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenModal()}
                >
                  Nueva Sucursal
                </Button>
              </Box>
              <TableContainer
                component={Paper}
                sx={{
                  width: '100%',
                  overflowX: 'auto',
                  boxShadow: 3,
                }}
              >
                <Table
                  sx={{
                    tableLayout: 'auto',
                  }}
                  aria-label="tabla de sucursales"
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        ID
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Nombre Sucursal
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Veterinaria
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Es No Pago
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Teléfonos
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Dirección
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Horario
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        IP Privada
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Latitud
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Longitud
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Creado
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Actualizado
                      </TableCell>
                      <TableCell sx={{ fontWeight: 'bold', textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                        Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedSucursales.map((row) => {
                      if (!row || !row.id_sucursal) {
                        console.warn('Fila inválida en la tabla:', row);
                        return null;
                      }
                      return (
                        <TableRow
                          key={row.id_sucursal}
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.id_sucursal}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 }, fontWeight: 'bold' }}>
                            {row.nombre_sucursal}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {pets.find(p => p.id_pet === row.id_pet)?.nombre_veterinaria || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.es_nopago ? 'Sí' : 'No'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.telefonos_sucursal || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.direccion_sucursal || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.horario_sucursal || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.ip_privada || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.latitud || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {row.longitud || 'N/A'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {formatDate(row.createdAt)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            {formatDate(row.updatedAt)}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center', px: { xs: 1, sm: 2 } }}>
                            <IconButton
                              onClick={() => handleOpenModal(row)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => handleDelete(row.id_sucursal)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 2 }}>
                <Button
                  variant="contained"
                  disabled={page === 0}
                  onClick={() => handleChangePage(page - 1)}
                >
                  Anterior
                </Button>
                <Typography sx={{ alignSelf: 'center' }}>
                  Página {page + 1} de {Math.ceil(sucursales.length / rowsPerPage)}
                </Typography>
                <Button
                  variant="contained"
                  disabled={page >= Math.ceil(sucursales.length / rowsPerPage) - 1}
                  onClick={() => handleChangePage(page + 1)}
                >
                  Siguiente
                </Button>
              </Box>
            </Box>
          </Box>
        )}
        <Dialog open={openModal} onClose={() => setOpenModal(false)}>
          <DialogTitle>{selectedSucursal ? 'Editar Sucursal' : 'Nueva Sucursal'}</DialogTitle>
          <DialogContent>
            <TextField
              label="Nombre Sucursal"
              fullWidth
              value={formData.nombre_sucursal}
              onChange={(e) => setFormData({ ...formData, nombre_sucursal: e.target.value })}
              sx={{ mt: 2 }}
              required
            />
            <TextField
              select
              label="Veterinaria"
              fullWidth
              value={formData.id_pet}
              onChange={(e) => setFormData({ ...formData, id_pet: e.target.value })}
              sx={{ mt: 2 }}
              required
            >
              {pets.length > 0 ? (
                pets.map((pet) => (
                  <MenuItem key={pet.id_pet} value={pet.id_pet}>
                    {pet.nombre_veterinaria}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No hay veterinarias disponibles
                </MenuItem>
              )}
            </TextField>
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
              <Switch
                checked={formData.es_nopago}
                onChange={(e) => setFormData({ ...formData, es_nopago: e.target.checked })}
                color="primary"
              />
              <Typography>Es No Pago</Typography>
            </Box>
            <TextField
              label="Teléfonos"
              fullWidth
              value={formData.telefonos_sucursal}
              onChange={(e) => setFormData({ ...formData, telefonos_sucursal: e.target.value })}
              sx={{ mt: 2 }}
            />
            <TextField
              label="Dirección"
              fullWidth
              value={formData.direccion_sucursal}
              onChange={(e) => setFormData({ ...formData, direccion_sucursal: e.target.value })}
              sx={{ mt: 2 }}
            />
            <TextField
              label="Horario"
              fullWidth
              value={formData.horario_sucursal}
              onChange={(e) => setFormData({ ...formData, horario_sucursal: e.target.value })}
              sx={{ mt: 2 }}
            />
            <TextField
              label="IP Privada"
              fullWidth
              value={formData.ip_privada}
              onChange={(e) => setFormData({ ...formData, ip_privada: e.target.value })}
              sx={{ mt: 2 }}
            />
            <TextField
              label="Latitud"
              fullWidth
              type="number"
              value={formData.latitud}
              onChange={(e) => setFormData({ ...formData, latitud: e.target.value })}
              sx={{ mt: 2 }}
            />
            <TextField
              label="Longitud"
              fullWidth
              type="number"
              value={formData.longitud}
              onChange={(e) => setFormData({ ...formData, longitud: e.target.value })}
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)}>Cancelar</Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!formData.nombre_sucursal || !formData.id_pet}
            >
              Guardar
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          sx={{ zIndex: 1500 }}
        >
          <Alert onClose={handleSnackbarClose} severity={error ? 'error' : 'success'} sx={{ width: '100%' }}>
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default Sucursales;